<script setup>
import { computed, reactive, onMounted, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import TargetAndArrow from '../../assets/scenes/TargetAndArrow.svg';
import AppFooter from '../organisms/AppFooter.vue';
import LayoutDefault from '../layouts/LayoutDefault.vue';
import AppLink from '../atoms/app/AppLink.vue';
import { useJobDataStore } from '../../stores/job-store';
import AppIcon from '../atoms/app/AppIcon.vue';
import { useApplicationStore } from '../../stores/application-store';
import { useBreakpoints } from '../../composables/breakpoint';
import MoreJobsSection from '../organisms/MoreJobsSection.vue';
import ApplicationSummary from '../organisms/ApplicationSummary.vue';
import { formatLocations } from '../../utils/format-locations';
import { track } from '../../utils/tracking';
import { useAuth } from '../../stores/auth-store';
import { getQueryToSaveApplicationAfterAuth } from '../../utils/saveApplicationAfterAuth';
import TextBase from "../atoms/app/text/TextBase.vue";
import TextHeadline from "../atoms/app/text/TextHeadline.vue";

const router = useRouter();
const user = useAuth();
const job = useJobDataStore();
const application = useApplicationStore();
const { cvExtractionUsed } = storeToRefs(application);
const { isMobile, isMediumScreen } = useBreakpoints();

/* Stepper */
const openSteps = reactive({
    applyToTheJob: user.isLoggedIn,
    saveYourApplication: !user.isLoggedIn,
    waitingForFeedback: false,
});

const toggleStep = (step) => {
    if (!isMobile.value) return;

    const stepStatus = openSteps[step];
    Object.keys(openSteps).forEach((key) => { openSteps[key] = false; });

    openSteps[step] = !stepStatus;

    // Track if step is open now
    if (openSteps[step]) {
        const stepName = step.replace(/([A-Z])/g, ' $1').toLowerCase(); // convert 'stepName' to 'step name'
        track({
            element: 'AP_F: Successpage',
            elementDetail: `Open step "${stepName}" mobile`,
        });
    }
};

/* Related Jobs */
const relatedJobInfoHeadline = computed(() => {
    if (isMobile.value) return 'Ähnliche Jobs in der Nähe';

    let headline = 'Ähnliche Jobs';
    if (!job.locations) return headline;

    headline = `${headline} in ${formatLocations(job.locations)}`;
    return headline;
});

async function saveApplication(e) {
    if (e) e.preventDefault();
    const query = getQueryToSaveApplicationAfterAuth();

    await router.push({
        name: 'login',
        query,
    });
}

const applyToTheJob = ref(null);
const saveYourApplication = ref(null);

onMounted(() => {
    // setTimeout is needed, because, 'scrollIntoView' is called before Vue renders the page.
    // nextTick() proved to be insufficient.
    setTimeout(() => {
        if (!isMobile.value) return;
        if (openSteps.applyToTheJob) {
            applyToTheJob.value?.scrollIntoView({ behavior: 'smooth' });
        } else if (openSteps.saveYourApplication) {
            saveYourApplication.value?.scrollIntoView({ behavior: 'smooth' });
        }
    });

    setTimeout(() => {
        if (cvExtractionUsed.value) {
            window.dataLayer?.push({
                event: 'hotjar_trigger',
                trigger: 'cv-extraction-survey',
            });
        }
    }, 200);
});

</script>

<template>
    <LayoutDefault>
        <template #main>
            <div class="p-pageSuccess splitview:flex splitview:gap-3xl l:gap-5xl">
                <div
                    class="flex flex-col text-center mb-l
                           splitview:text-start splitview:items-start splitview:flex-shrink-0 splitview:w-[340px]"
                >
                    <div class="splitview:sticky splitview:top-0 splitview:mb-xl mt-2xl">
                        <TextHeadline
                            level="1"
                            variant="lg-500"
                            class="mb-s"
                        >
                            Geschafft! Bewerbung versendet
                        </TextHeadline>
                        <p class="mb-xl">
                            Eine Recruiter*in wird sich in den nächsten Tagen bei dir melden.
                            In der Zwischenzeit kannst du andere Jobs entdecken.
                        </p>
                        <div
                            class="flex flex-col m:flex-row gap-s m:gap-l justify-center splitview:justify-start mb-xl"
                        >
                            <a
                                class="link"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-qa="discover similar jobs"
                                data-gtm-element="AP_F: Successpage"
                                data-gtm-element-detail="Job search"
                                :href="job.jobPageUrl"
                            >
                                Weitere Jobs entdecken
                            </a>
                            <AppLink
                                v-if="user.isLoggedIn"
                                class="p-m block"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-qa="open application"
                                data-gtm-element="AP_F: Successpage"
                                data-gtm-element-detail="See application"
                                :href="router.resolve({ name: 'bewerbungen' }).href"
                            >
                                Zur Bewerbung
                            </AppLink>
                        </div>
                    </div>
                </div>
                <div class="splitview:mt-4xl">
                    <div class="flex gap-l splitview:gap-xl relative pb-xl m:pb-3xl">
                        <div>
                            <div
                                class="rounded-full w-2xl aspect-square bg-gray-900
                                       flex items-center mx-auto justify-center relative z-10"
                            >
                                <AppIcon
                                    class="text-white"
                                    name="check"
                                />
                            </div>
                        </div>
                        <div
                            ref="applyToTheJob"
                            class="pt-xs flex-grow scroll-mt-xl ml-[-3rem] m:ml-0 overflow-hidden"
                        >
                            <button
                                class="flex justify-between gap-l w-full pl-[3rem] m:pl-0"
                                :class="isMediumScreen && 'cursor-default'"
                                type="button"
                                aria-label="Bewirb dich auf die Stelle - erledigt!"
                                data-qa="toggle apply to job"
                                @click="toggleStep('applyToTheJob')"
                            >
                                <TextBase
                                    as="h2"
                                    variant="base-500"
                                >
                                    Bewirb dich auf deinen {{ isMobile ? 'Job' : 'Traumjob' }}
                                </TextBase>
                                <AppIcon
                                    v-if="isMobile"
                                    class="text-gray-900 transition-transform"
                                    :class="openSteps.applyToTheJob && 'rotate-180'"
                                    size="xl"
                                    name="arrow-down"
                                />
                            </button>
                            <div
                                v-if="isMediumScreen || openSteps.applyToTheJob"
                                data-qa="apply to the job content"
                            >
                                <TextBase
                                    as="p"
                                    class="pl-[3rem] m:pl-0 !text-gray-600"
                                >
                                    Deine Unterlagen wurden erfolgreich an die Recruiter*in übermittelt. Es wurde eine
                                    Zusammenfassung an <strong>{{ application.email }}</strong> geschickt.
                                </TextBase>
                                <div class="gap-l relative">
                                    <div
                                        class="pointer-events-none block m:hidden absolute z-10
                                            bg-gradient-to-t from-white h-4xl top-[-64px] left-0 w-[30px]"
                                    />
                                    <ApplicationSummary
                                        is-success-page
                                    />
                                    <div
                                        class="pointer-events-none block m:hidden absolute z-10
                                            bg-gradient-to-b from-white h-l bottom-[-16px] left-0 w-[30px]"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="absolute bg-gray-100 w-[2px] h-full left-l"/>
                    </div>
                    <div
                        v-if="!user.isLoggedIn"
                        class="flex gap-l splitview:gap-xl relative pb-xl m:pb-3xl"
                    >
                        <div>
                            <div
                                class="rounded-full w-2xl aspect-square border border-gray-300 bg-white border-solid
                                       flex items-center mx-auto justify-center relative z-10"
                            >
                                <span class="text-gray-400 text-l">2</span>
                            </div>
                        </div>
                        <div
                            ref="saveYourApplication"
                            class="pt-xs flex-grow scroll-mt-xl"
                        >
                            <button
                                class="flex justify-between gap-l w-full"
                                :class="isMediumScreen && 'cursor-default'"
                                type="button"
                                aria-label="Bewerbung wird gesichtet - Infos"
                                @click="toggleStep('saveYourApplication')"
                            >
                                <TextBase
                                    variant="base-500"
                                >
                                    Speichere die Bewerbung ab
                                </TextBase>
                                <AppIcon
                                    v-if="isMobile"
                                    class="text-gray-900 transition-transform"
                                    :class="openSteps.saveYourApplication && 'rotate-180'"
                                    size="xl"
                                    name="arrow-down"
                                />
                            </button>
                            <div v-if="isMediumScreen || openSteps.saveYourApplication">
                                <TextBase class="mb-xl !text-gray-600">
                                    Behalte die Übersicht über deinen Bewerbungsstatus mit deinem karriere.at-Profil.
                                </TextBase>
                                <div class="bg-gradient-prism rounded-sm flex relative items-center justify-between">
                                    <div class="p-xl flex-col inline-flex m:flex-shrink-0 relative z-10">
                                        <TextHeadline
                                            variant="md-400"
                                            class="mb-m"
                                        >
                                            Deine Vorteile mit einem karriere.at-Profil
                                        </TextHeadline>
                                        <ul class="mb-xl space-y-xs">
                                            <li class="flex gap-m items-center">
                                                <AppIcon
                                                    class="text-black flex-shrink-0"
                                                    name="check"
                                                />
                                                Übersicht über abgeschickte Bewerbungen
                                            </li>
                                            <li class="flex gap-m items-center">
                                                <AppIcon
                                                    class="text-black flex-shrink-0"
                                                    name="check"
                                                />
                                                Die nächste Bewerbung geht schneller
                                            </li>
                                            <li class="hidden m:flex gap-m items-center">
                                                <AppIcon
                                                    class="text-black flex-shrink-0"
                                                    name="check"
                                                />
                                                Passende Jobs per Mail erhalten
                                            </li>
                                            <li class="hidden m:flex gap-m items-center">
                                                <AppIcon
                                                    class="text-black flex-shrink-0"
                                                    name="check"
                                                />
                                                Jobs über deine Merkliste schnell wiederfinden
                                            </li>
                                            <li class="flex gap-m items-center">
                                                <AppIcon
                                                    class="text-black flex-shrink-0"
                                                    name="check"
                                                />
                                                Werde entdeckt und erhalte Jobangebote
                                            </li>
                                        </ul>
                                        <!--eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events-->
                                        <a
                                            class="button button-green text-center w-[230px] button-transparent"
                                            data-gtm-element="AP_F: Successpage"
                                            data-gtm-element-detail="Save application"
                                            href="#"
                                            @click="saveApplication"
                                        >
                                            Bewerbung speichern
                                        </a>
                                    </div>
                                    <div class="hidden m:block flex-shrink">
                                        <TargetAndArrow
                                            class="size-[192px] mr-10"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            class="
                                            absolute
                                            bg-gray-100
                                            w-[2px]
                                            h-full
                                            left-l"
                        />
                    </div>
                    <div class="flex gap-l splitview:gap-xl relative pb-xl m:pb-3xl">
                        <div>
                            <div
                                class="rounded-full w-2xl aspect-square border border-gray-300 bg-white border-solid
                                       flex items-center mx-auto justify-center relative z-10"
                            >
                                <span class="text-gray-400 text-l">{{ user.isLoggedIn ? '2' : '3' }}</span>
                            </div>
                        </div>
                        <div class="pt-xs flex-grow">
                            <button
                                class="flex justify-between gap-l w-full"
                                :class="isMediumScreen && 'cursor-default'"
                                type="button"
                                aria-label="Bewerbung wird gesichtet - Infos"
                                data-qa="toggle waiting for feedback"
                                @click="toggleStep('waitingForFeedback')"
                            >
                                <TextBase
                                    variant="base-500"
                                >
                                    Bewerbung wird gesichtet
                                </TextBase>
                                <AppIcon
                                    v-if="isMobile"
                                    class="text-gray-900 transition-transform"
                                    :class="openSteps.waitingForFeedback && 'rotate-180'"
                                    size="xl"
                                    name="arrow-down"
                                />
                            </button>
                            <div
                                v-if="isMediumScreen || openSteps.waitingForFeedback"
                                data-qa="waiting for feedback content"
                            >
                                <TextBase
                                    as="p"
                                    class="!text-gray-600"
                                >
                                    Üblicherweise meldet sich die Recruiter*in innerhalb von 10 Arbeitstagen bei dir.
                                </TextBase>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="p-pageSuccess__moreJobsSections overflow-hidden">
                <div
                    v-if="job.relatedJobs.length > 0"
                    class="p-pageSuccess__relatedJobs border-b border-solid border-gray-100 py-2xl"
                >
                    <MoreJobsSection
                        :headline="relatedJobInfoHeadline"
                        :link-url="job.jobPageUrl"
                        :job-items="job.relatedJobs"
                    />
                </div>
                <div
                    v-if="job.recentJobs.length > 0"
                    class="p-pageSuccess__relatedJobs border-b border-solid border-gray-100 py-2xl"
                >
                    <MoreJobsSection
                        headline="Diese Jobs hast du dir zuletzt angesehen"
                        last-seen
                        :job-items="job.recentJobs"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <AppFooter data-qa="big footer"/>
        </template>
    </LayoutDefault>
</template>

<style lang="scss">
@use 'sass:math';
@use '~@karriereat/global-styles/scss/settings/breakpoint';
@use '~@karriereat/global-styles/scss/settings/spacing';
@use '~@karriereat/global-styles/scss/tools/wrap';
@use '~@karriereat/global-styles/scss/components/button';

.p-pageSuccess {
    @include wrap.k-wrap();

    &__moreJobsSections {
        @include wrap.k-wrap();
    }
}

</style>
